import React, { useState, useEffect } from "react";
import TagService from "../services/TagService";
import { toast } from "react-toastify";
import "../style/pages/TagsPage.css";
// We can reuse the Create Location/Asset modal style or build a simple inline one
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

const TagsPage = () => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  const [currentTag, setCurrentTag] = useState({
    id: null,
    name: "",
    description: "",
    color: "#3b82f6" // Default blue
  });

  const fetchTags = async () => {
    try {
      setLoading(true);
      const data = await TagService.getAllTags();
      setTags(data);
    } catch (error) {
      toast.error("Failed to load tags");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentTag({ ...currentTag, [name]: value });
  };

  const openAddModal = () => {
    setCurrentTag({ id: null, name: "", description: "", color: "#3b82f6", userId: 1 }); // Assuming userId 1 for now, similar to other entities if AuthContext isn't piped in yet
    setIsEditing(false);
    setShowModal(true);
  };

  const openEditModal = (tag) => {
    setCurrentTag(tag);
    setIsEditing(true);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Get User ID from localStorage (standard HomeBox auth pattern)
      const user = JSON.parse(localStorage.getItem('user'));
      const tagData = { ...currentTag, userId: user?.id || 1 };

      if (isEditing) {
        await TagService.updateTag(tagData.id, tagData);
        toast.success("Tag updated successfully!");
      } else {
        await TagService.addTag(tagData);
        toast.success("Tag created successfully!");
      }
      closeModal();
      fetchTags();
    } catch (error) {
      toast.error("Failed to save tag");
      console.error(error);
    }
  };

  const handleDelete = (id) => {
    confirmAlert({
      title: 'Confirm to delete',
      message: 'Are you sure you want to delete this tag? This will remove it from all associated assets but will not delete the assets themselves.',
      buttons: [
        {
          label: 'Yes',
          onClick: async () => {
            try {
              await TagService.deleteTag(id);
              toast.success("Tag deleted successfully!");
              fetchTags();
            } catch (error) {
              toast.error("Failed to delete tag");
              console.error(error);
            }
          }
        },
        {
          label: 'No',
        }
      ]
    });
  };

  return (
    <div className="tags-page">
      <div className="tags-header">
        <h2>Manage Tags & Collections</h2>
        <button className="add-btn" onClick={openAddModal}>
          <i className="fa-solid fa-plus"></i> Add Tag
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading tags...</div>
      ) : tags.length === 0 ? (
        <div className="no-data">
            <i className="fa-solid fa-tags empty-icon"></i>
            <p>No tags found. Create your first tag to start categorizing assets!</p>
        </div>
      ) : (
        <div className="tags-grid">
          {tags.map((tag) => (
            <div className="tag-card" key={tag.id} style={{ borderTopColor: tag.color || '#3b82f6' }}>
              <div className="tag-card-content">
                <div className="tag-badge" style={{ backgroundColor: tag.color || '#3b82f6' }}>
                   {tag.name}
                </div>
                <p className="tag-desc">{tag.description || "No description provided."}</p>
              </div>
              <div className="tag-card-actions">
                <button className="action-btn edit" onClick={() => openEditModal(tag)} title="Edit">
                  <i className="fa-solid fa-pen"></i>
                </button>
                <button className="action-btn delete" onClick={() => handleDelete(tag.id)} title="Delete">
                  <i className="fa-solid fa-trash"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Basic Inline Modal for Tag Form */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content tag-modal">
            <h3>{isEditing ? "Edit Tag" : "Create New Tag"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Tag Name *</label>
                <input
                  type="text"
                  name="name"
                  value={currentTag.name}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g. Camping Gear, Broken, Lent to David"
                  className="form-control"
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={currentTag.description || ""}
                  onChange={handleInputChange}
                  placeholder="Optional description"
                  className="form-control"
                  rows="3"
                ></textarea>
              </div>

              <div className="form-group">
                <label>Tag Color</label>
                <div className="color-picker-container">
                    <input
                    type="color"
                    name="color"
                    value={currentTag.color}
                    onChange={handleInputChange}
                    className="color-input"
                    />
                    <span className="color-hex">{currentTag.color}</span>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  {isEditing ? "Update Tag" : "Save Tag"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagsPage;
